import type { GetStudyRunResultsResponse } from "@/types/responses";

import {
  decodeExpect,
  decodeRecall,
  identifyQuestion,
  type QuestionKey,
  voteCodes,
} from "./survey";
import type {
  AreaType,
  Gender,
  Office,
  OfficeAnswers,
  Respondent,
  RunSample,
  Verbatim,
  VoteCode,
} from "./types";

/* ── Constants ─────────────────────────────────── */

const MAX_VERBATIMS = 12;

/* Persona demographics come back as localized labels (en/fr/es). */
const FEMALE = /^(female|femme|mujer)/i;
const MALE = /^(male|homme|hombre)/i;
const SUBURBAN = /^(suburban|périurbain|periurbain|suburbano)/i;
const URBAN = /^(urban|urbain|urbano)/i;
const RURAL = /^rural/i;
const COLLEGE =
  /(bachelor|master|doctor|licence|licenciatura|grado|máster|doctorat|doctorado)/i;

/* ── Helpers ───────────────────────────────────── */

type Persona = GetStudyRunResultsResponse["personas"][string];
type WireAnswer =
  GetStudyRunResultsResponse["results"][number]["groups"][number]["answers"][number];

const genderOf = (label: string): Gender =>
  FEMALE.test(label) ? "female" : MALE.test(label) ? "male" : "other";

const areaOf = (label: string): AreaType | null =>
  SUBURBAN.test(label)
    ? "suburban"
    : URBAN.test(label)
      ? "urban"
      : RURAL.test(label)
        ? "rural"
        : null;

const choiceOf = (answer: WireAnswer): number | null =>
  answer.answer.type === "single_choice" ? answer.answer.choice : null;

const emptyRespondent = (persona: Persona): Respondent => {
  const county = persona.residency.county_code;
  return {
    age: persona.general.age,
    approval: null,
    area: areaOf(persona.residency.type),
    college: COLLEGE.test(persona.education.level),
    county: county || null,
    finances: null,
    gender: genderOf(persona.general.gender),
    issue: null,
    neighbors2024: null,
    offices: {},
    recall: null,
    turnout: null,
  };
};

const officeAnswers = (
  respondent: Respondent,
  office: Office
): OfficeAnswers => {
  const existing = respondent.offices[office];
  if (existing) return existing;
  const created: OfficeAnswers = { expect: null, neighbors: null, vote: null };
  respondent.offices[office] = created;
  return created;
};

const applyChoice = (
  respondent: Respondent,
  key: QuestionKey,
  choice: number,
  codes: VoteCode[]
) => {
  switch (key) {
    case "approval":
      respondent.approval = choice;
      return;
    case "finances":
      respondent.finances = choice;
      return;
    case "issue":
      respondent.issue = choice;
      return;
    case "neighbors2024":
      respondent.neighbors2024 = choice;
      return;
    case "recall":
      respondent.recall = decodeRecall(choice);
      return;
    case "turnout":
      respondent.turnout = choice;
      return;
    case "why":
      return;
  }
  const [office, field] = key.split(".") as [
    Office,
    "expect" | "neighbors" | "vote",
  ];
  const answers = officeAnswers(respondent, office);
  if (field === "vote") answers.vote = codes[choice] ?? null;
  if (field === "expect") answers.expect = decodeExpect(choice);
  if (field === "neighbors") answers.neighbors = choice;
};

/* ── Decoder ───────────────────────────────────── */

/**
 * Turns one run's results (every question, every persona) into compact
 * respondents. Questions are recognized by their text, so the decoder
 * survives questionnaire edits that keep the stems.
 */
export const decodeRun = (
  response: GetStudyRunResultsResponse,
  meta: { state: string; studyId: string; wave: number }
): RunSample => {
  const respondents = new Map<string, Respondent>();
  const countyNames: Record<string, string> = {};
  const offices = new Set<Office>();
  const questionIds: Partial<Record<Office, string>> = {};
  const whyByPersona = new Map<string, string>();

  const respondentFor = (personaId: string): Respondent | null => {
    const existing = respondents.get(personaId);
    if (existing) return existing;
    const persona = response.personas[personaId];
    if (!persona) return null;
    const created = emptyRespondent(persona);
    if (created.county && persona.residency.county) {
      countyNames[created.county] = persona.residency.county;
    }
    respondents.set(personaId, created);
    return created;
  };

  for (const result of response.results) {
    // Study questions always have a text; only adhoc questions can lack one.
    const key = result.question.text
      ? identifyQuestion(result.question.text)
      : null;
    if (!key) continue;
    if (key.includes(".")) {
      const [office, field] = key.split(".") as [
        Office,
        "expect" | "neighbors" | "vote",
      ];
      offices.add(office);
      if (field === "vote") questionIds[office] = result.question.id;
    }

    const codes = voteCodes(result.question.options);
    for (const group of result.groups) {
      for (const answer of group.answers) {
        const respondent = respondentFor(answer.persona_id);
        if (!respondent) continue;
        if (key === "why") {
          const text = answer.answer.translated_text ?? answer.answer.text;
          if (text) whyByPersona.set(answer.persona_id, text.trim());
          continue;
        }
        const choice = choiceOf(answer);
        if (choice !== null) applyChoice(respondent, key, choice, codes);
      }
    }
  }

  const verbatims: Verbatim[] = [];
  for (const [personaId, quote] of whyByPersona) {
    if (verbatims.length >= MAX_VERBATIMS) break;
    const respondent = respondents.get(personaId);
    if (!respondent || quote.length < 20) continue;
    verbatims.push({
      age: respondent.age,
      county: respondent.county
        ? (countyNames[respondent.county] ?? null)
        : null,
      gender: respondent.gender,
      quote,
      vote:
        respondent.offices.senate?.vote ??
        respondent.offices.governor?.vote ??
        respondent.offices.house?.vote ??
        null,
    });
  }

  return {
    completedAt: response.run.completed_at ?? response.run.queued_at,
    countyNames,
    offices: [...offices].sort(),
    questionIds,
    respondents: [...respondents.values()],
    runId: response.run.id,
    state: meta.state,
    studyId: meta.studyId,
    verbatims,
    wave: meta.wave,
  };
};
